<template>
    <div class="MultiFieldCriterionRecords">
        <div
            class="MultiFieldCriterionRecords__record"
            :data-test-id="`MultiFieldCriterion__${name}Record`"
        >
            <span
                v-if="showIndex"
                class="MultiFieldCriterionRecords__index mr-2"
                :class="additionalIndexClass"
            >
                {{ index + 1 }}
            </span>
            <div class="MultiFieldCriterionRecords__recordContent">
                <div class="MultiFieldCriterionRecords__title">
                    <EidIcon
                        v-if="isUmbrella"
                        v-tooltip="{ popperClass: 'VTooltip--dark', content: umbrellaTooltipContent }"
                        class="MultiFieldCriterionRecords__umbrellaIcon"
                        data-test-id="MultiFieldCriterionRecords__umbrellaIcon"
                        :icon-src="umbrellaIconSrc"
                        alt="Umbrella"
                    />
                    {{ startCase(name) }}
                </div>
                <div class="MultiFieldCriterionRecords__valueWithError">
                    <div class="MultiFieldCriterionRecords__value">
                        {{ getFieldValue(value) }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
    .MultiFieldCriterionRecords {
        &__title {
            display: flex;
            align-items: center;
        }

        &__value {
            display: flex;
            align-items: center;
        }

        &__umbrellaIcon {
            width: 14px;
            height: 14px;
            margin-right: 6px;
            flex-shrink: 0;
        }
    }
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import startCase from 'lodash/startCase';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { TprmRequirementType } from '@evidentid/tprm-portal-lib/models/entity-details';

    import { currencyFormatter } from '@/modules/entity-details/utils/currencyFormatter';
    import { getRequirementTypeIconUri } from '@/modules/entity-details/utils/getRequirementTypeIconUri';

    const UMBRELLA_EXCESS_LIABILITY = 'UMBRELLA_EXCESS_LIABILITY' as TprmRequirementType;

    export default defineComponent({
        name: 'MultiFieldCriterionRecords',
        components: { EidIcon },
        props: {
            name: {
                type: String as PropType<string>,
                default: '',
            },
            value: {
                type: [ String, Number, Boolean, Array ] as PropType<unknown>,
                required: true,
            },
            index: {
                type: Number as PropType<number>,
                default: 1,
            },
            showIndex: {
                type: Boolean as PropType<Boolean>,
                default: false,
            },
            additionalIndexClass: {
                type: Array as PropType<string[]>,
                default: (): string[] => [],
            },
            coverageType: {
                type: String as PropType<string>,
                default: '',
            },
            requirementCoverageType: {
                type: String as PropType<string>,
                default: '',
            },
            currency: {
                type: String as PropType<string>,
                default: '',
            },
        },
        computed: {
            isUmbrella(): boolean {
                return this.coverageType === UMBRELLA_EXCESS_LIABILITY
                    && this.coverageType !== this.requirementCoverageType;
            },
            umbrellaIconSrc(): string {
                return getRequirementTypeIconUri(UMBRELLA_EXCESS_LIABILITY);
            },
            umbrellaTooltipContent(): string {
                return 'This value comes from Umbrella coverage, not this requirement.';
            },
        },
        methods: {
            getFieldValue(value: unknown): unknown {
                if (Array.isArray(value)) {
                    return value.join(', ');
                }
                if (typeof value === 'number') {
                    // Formats with the field's own source currency when known; a field pulled in
                    // from another coverage (e.g. Umbrella) may belong to a differently-currencied
                    // policy than the one being rendered. Falls back to plain digit grouping
                    // rather than assuming a currency, since an unresolved one is more likely
                    // missing data than USD.
                    // eventually we might consider taking out en-US to support different countries when i18n is needed
                    return this.currency
                        ? currencyFormatter(this.currency).format(value)
                        : value.toLocaleString('en-US', { maximumFractionDigits: 0 });
                }
                return value;
            },
            startCase,
        },
    });
</script>
